import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../utils/api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { 
  Smartphone, 
  Monitor, 
  RefreshCw, 
  TrendingUp,
  Download,
  Users,
  Activity,
  Clock,
  Apple,
  Layers,
  BarChart3,
  Calendar,
  ChevronDown,
  ChevronUp
} from 'lucide-react';

// Android Icon Component
const AndroidIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.523 15.341c-.5 0-.9-.4-.9-.9s.4-.9.9-.9.9.4.9.9-.4.9-.9.9zm-11.046 0c-.5 0-.9-.4-.9-.9s.4-.9.9-.9.9.4.9.9-.4.9-.9.9zm11.405-6.02l1.9-3.293c.11-.19.045-.433-.145-.544-.19-.11-.433-.045-.544.145l-1.923 3.33C15.605 8.186 13.876 7.74 12 7.74s-3.605.446-5.17 1.219L4.907 5.63c-.11-.19-.353-.256-.544-.145-.19.11-.256.354-.145.544l1.9 3.293C3.439 10.81 1.5 13.513 1.5 16.609h21c0-3.096-1.939-5.8-4.618-7.288z"/>
  </svg>
);

const AppStatistics = () => {
  const { isAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [showAllDevices, setShowAllDevices] = useState(false);
  const [updatesHistory, setUpdatesHistory] = useState(null);

  useEffect(() => {
    if (isAdmin()) {
      fetchStats();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const [statsRes, updatesRes] = await Promise.all([
        api.get('/app-stats/overview'),
        api.get('/app-stats/updates-history')
      ]);
      setStats(statsRes.data);
      setUpdatesHistory(updatesRes.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
      toast.error('فشل تحميل الإحصائيات');
    } finally {
      setLoading(false);
    }
  };

  if (!isAdmin()) {
    return (
      <div className="text-center py-12">
        <p className="text-xl text-muted-foreground">ليس لديك صلاحية للوصول إلى هذه الصفحة</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  const summary = stats?.summary || {};
  const platforms = stats?.platforms || {};
  const versionDist = stats?.version_distribution || {};

  return (
    <div className="space-y-8" data-testid="app-statistics-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <BarChart3 className="w-8 h-8 text-primary" />
            إحصائيات التطبيق
          </h1>
          <p className="text-muted-foreground mt-2">تتبع تحميلات وتحديثات التطبيق على جميع المنصات</p>
        </div>
        <Button onClick={fetchStats} variant="outline" className="gap-2">
          <RefreshCw className="w-4 h-4" />
          تحديث
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-card border border-white/10 rounded-xl p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="bg-primary/20 p-2 rounded-lg">
              <Smartphone className="w-5 h-5 text-primary" />
            </div>
            <span className="text-sm text-muted-foreground">إجمالي الأجهزة</span>
          </div>
          <p className="text-3xl font-bold text-primary">{summary.total_devices || 0}</p>
        </div>

        <div className="bg-card border border-white/10 rounded-xl p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="bg-green-500/20 p-2 rounded-lg">
              <Activity className="w-5 h-5 text-green-500" />
            </div>
            <span className="text-sm text-muted-foreground">أجهزة نشطة</span>
          </div>
          <p className="text-3xl font-bold text-green-500">{summary.active_devices || 0}</p>
          <p className="text-xs text-muted-foreground mt-1">آخر 7 أيام</p>
        </div>

        <div className="bg-card border border-white/10 rounded-xl p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="bg-blue-500/20 p-2 rounded-lg">
              <TrendingUp className="w-5 h-5 text-blue-500" />
            </div>
            <span className="text-sm text-muted-foreground">تحميلات جديدة</span>
          </div>
          <p className="text-3xl font-bold text-blue-500">{summary.new_this_week || 0}</p>
          <p className="text-xs text-muted-foreground mt-1">هذا الأسبوع</p>
        </div>

        <div className="bg-card border border-white/10 rounded-xl p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="bg-yellow-500/20 p-2 rounded-lg">
              <RefreshCw className="w-5 h-5 text-yellow-500" />
            </div>
            <span className="text-sm text-muted-foreground">إجمالي التحديثات</span>
          </div>
          <p className="text-3xl font-bold text-yellow-500">{summary.total_updates || 0}</p>
        </div>
      </div>

      {/* Platform Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Android */}
        <div className="bg-gradient-to-br from-green-500/10 to-green-600/5 border border-green-500/20 rounded-xl p-6">
          <div className="flex items-center gap-4 mb-6">
            <div className="bg-green-500/20 p-3 rounded-xl">
              <AndroidIcon className="w-8 h-8 text-green-500" />
            </div>
            <div>
              <h3 className="text-xl font-bold">Android</h3>
              <p className="text-sm text-muted-foreground">أجهزة أندرويد</p>
            </div>
          </div>
          
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-muted-foreground">التحميلات</span>
              <span className="text-2xl font-bold text-green-500">{summary.total_android || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-muted-foreground">PWA</span>
              <span className="font-medium">{platforms.android?.pwa || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-muted-foreground">التحديثات</span>
              <span className="font-medium">{platforms.android?.updates || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-muted-foreground">الجلسات</span>
              <span className="font-medium">{platforms.android?.sessions || 0}</span>
            </div>
          </div>
          
          {/* Progress bar */}
          <div className="mt-6">
            <div className="h-2 bg-green-500/20 rounded-full overflow-hidden">
              <div 
                className="h-full bg-green-500 rounded-full transition-all duration-500"
                style={{ width: `${summary.total_devices ? (summary.total_android / summary.total_devices) * 100 : 0}%` }}
              ></div>
            </div>
            <p className="text-xs text-muted-foreground mt-2 text-center">
              {summary.total_devices ? Math.round((summary.total_android / summary.total_devices) * 100) : 0}% من الإجمالي
            </p>
          </div>
        </div>

        {/* iOS */}
        <div className="bg-gradient-to-br from-gray-500/10 to-gray-600/5 border border-gray-500/20 rounded-xl p-6">
          <div className="flex items-center gap-4 mb-6">
            <div className="bg-gray-500/20 p-3 rounded-xl">
              <Apple className="w-8 h-8 text-gray-400" />
            </div>
            <div>
              <h3 className="text-xl font-bold">iOS</h3>
              <p className="text-sm text-muted-foreground">أجهزة آيفون وآيباد</p>
            </div>
          </div>
          
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-muted-foreground">التحميلات</span>
              <span className="text-2xl font-bold text-gray-400">{summary.total_ios || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-muted-foreground">PWA</span>
              <span className="font-medium">{platforms.ios?.pwa || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-muted-foreground">التحديثات</span>
              <span className="font-medium">{platforms.ios?.updates || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-muted-foreground">الجلسات</span>
              <span className="font-medium">{platforms.ios?.sessions || 0}</span>
            </div>
          </div>
          
          {/* Progress bar */}
          <div className="mt-6">
            <div className="h-2 bg-gray-500/20 rounded-full overflow-hidden">
              <div 
                className="h-full bg-gray-400 rounded-full transition-all duration-500"
                style={{ width: `${summary.total_devices ? (summary.total_ios / summary.total_devices) * 100 : 0}%` }}
              ></div>
            </div>
            <p className="text-xs text-muted-foreground mt-2 text-center">
              {summary.total_devices ? Math.round((summary.total_ios / summary.total_devices) * 100) : 0}% من الإجمالي
            </p>
          </div>
        </div>

        {/* Web */}
        <div className="bg-gradient-to-br from-blue-500/10 to-blue-600/5 border border-blue-500/20 rounded-xl p-6">
          <div className="flex items-center gap-4 mb-6">
            <div className="bg-blue-500/20 p-3 rounded-xl">
              <Monitor className="w-8 h-8 text-blue-500" />
            </div>
            <div>
              <h3 className="text-xl font-bold">Web</h3>
              <p className="text-sm text-muted-foreground">متصفح الويب</p>
            </div>
          </div>
          
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-muted-foreground">الزيارات</span>
              <span className="text-2xl font-bold text-blue-500">{summary.total_web || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-muted-foreground">PWA</span>
              <span className="font-medium">{platforms.web?.pwa || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-muted-foreground">التحديثات</span>
              <span className="font-medium">{platforms.web?.updates || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-muted-foreground">الجلسات</span>
              <span className="font-medium">{platforms.web?.sessions || 0}</span>
            </div>
          </div>
          
          {/* Progress bar */}
          <div className="mt-6">
            <div className="h-2 bg-blue-500/20 rounded-full overflow-hidden">
              <div 
                className="h-full bg-blue-500 rounded-full transition-all duration-500"
                style={{ width: `${summary.total_devices ? (summary.total_web / summary.total_devices) * 100 : 0}%` }}
              ></div>
            </div>
            <p className="text-xs text-muted-foreground mt-2 text-center">
              {summary.total_devices ? Math.round((summary.total_web / summary.total_devices) * 100) : 0}% من الإجمالي
            </p>
          </div>
        </div>
      </div>

      {/* PWA Stats */}
      <div className="bg-card border border-white/10 rounded-xl p-6">
        <div className="flex items-center gap-3 mb-6">
          <Download className="w-5 h-5 text-primary" />
          <h3 className="text-xl font-semibold">تحميلات PWA</h3>
        </div>
        
        <div className="flex items-center justify-center gap-8">
          <div className="text-center">
            <p className="text-5xl font-bold text-primary">{summary.total_pwa || 0}</p>
            <p className="text-sm text-muted-foreground mt-2">إجمالي تثبيتات PWA</p>
          </div>
          
          <div className="h-24 w-px bg-white/10"></div>
          
          <div className="text-center">
            <p className="text-3xl font-bold text-green-500">
              {summary.total_devices ? Math.round((summary.total_pwa / summary.total_devices) * 100) : 0}%
            </p>
            <p className="text-sm text-muted-foreground mt-2">نسبة التثبيت</p>
          </div>
        </div>
      </div>

      {/* Version Distribution */}
      <div className="bg-card border border-white/10 rounded-xl p-6">
        <div className="flex items-center gap-3 mb-6">
          <Layers className="w-5 h-5 text-primary" />
          <h3 className="text-xl font-semibold">توزيع الإصدارات</h3>
        </div>
        
        {Object.keys(versionDist).length > 0 ? (
          <div className="space-y-4">
            {Object.entries(versionDist).map(([version, data]) => (
              <div key={version} className="p-4 bg-secondary/30 rounded-lg">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <span className="text-lg font-bold">v{version}</span>
                    <span className="text-sm text-muted-foreground">({data.total} جهاز)</span>
                  </div>
                  <span className="text-sm text-primary">
                    {summary.total_devices ? Math.round((data.total / summary.total_devices) * 100) : 0}%
                  </span>
                </div>
                
                <div className="h-2 bg-secondary rounded-full overflow-hidden mb-3">
                  <div 
                    className="h-full bg-primary rounded-full transition-all duration-500"
                    style={{ width: `${summary.total_devices ? (data.total / summary.total_devices) * 100 : 0}%` }}
                  ></div>
                </div>
                
                <div className="flex gap-4 text-xs">
                  <span className="flex items-center gap-1">
                    <AndroidIcon className="w-3 h-3 text-green-500" />
                    {data.android || 0}
                  </span>
                  <span className="flex items-center gap-1">
                    <Apple className="w-3 h-3 text-gray-400" />
                    {data.ios || 0}
                  </span>
                  <span className="flex items-center gap-1">
                    <Monitor className="w-3 h-3 text-blue-500" />
                    {data.web || 0}
                  </span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            <Layers className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>لا توجد بيانات إصدارات متاحة</p>
          </div>
        )}
      </div>

      {/* Recent Updates */}
      {updatesHistory?.version_transitions?.length > 0 && (
        <div className="bg-card border border-white/10 rounded-xl p-6">
          <div className="flex items-center gap-3 mb-6">
            <RefreshCw className="w-5 h-5 text-yellow-500" />
            <h3 className="text-xl font-semibold">انتقالات الإصدارات</h3>
          </div>
          
          <div className="space-y-3">
            {updatesHistory.version_transitions.map((transition) => (
              <div key={`${transition._id.from}-${transition._id.to}`} className="flex items-center justify-between p-3 bg-secondary/30 rounded-lg">
                <div className="flex items-center gap-3">
                  <span className="text-muted-foreground">v{transition._id.from}</span>
                  <span className="text-primary">→</span>
                  <span className="font-bold">v{transition._id.to}</span>
                </div>
                <div className="flex items-center gap-4 text-sm">
                  <span className="text-muted-foreground">{transition.count} تحديث</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent Installs */}
      {stats?.recent_installs?.length > 0 && (
        <div className="bg-card border border-white/10 rounded-xl p-6">
          <div 
            className="flex items-center justify-between cursor-pointer"
            onClick={() => setShowAllDevices(!showAllDevices)}
          >
            <div className="flex items-center gap-3">
              <Users className="w-5 h-5 text-primary" />
              <h3 className="text-xl font-semibold">آخر التحميلات</h3>
            </div>
            <Button variant="ghost" size="sm">
              {showAllDevices ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </Button>
          </div>
          
          {showAllDevices && (
            <div className="mt-6 space-y-3">
              {stats.recent_installs.map((device) => (
                <div key={device.device_id || device.installed_at} className="flex items-center justify-between p-3 bg-secondary/30 rounded-lg">
                  <div className="flex items-center gap-3">
                    {device.platform === 'android' && <AndroidIcon className="w-5 h-5 text-green-500" />}
                    {device.platform === 'ios' && <Apple className="w-5 h-5 text-gray-400" />}
                    {device.platform === 'web' && <Monitor className="w-5 h-5 text-blue-500" />}
                    <div>
                      <span className="font-medium">{device.device_model || 'جهاز غير معروف'}</span>
                      <span className="text-sm text-muted-foreground ms-2">v{device.app_version}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 text-sm text-muted-foreground">
                    <span>{device.is_pwa ? 'PWA' : 'Web'}</span>
                    <span>{new Date(device.installed_at).toLocaleDateString('ar-SA')}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Info Box */}
      <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
        <p className="text-sm text-blue-400">
          <strong>ملاحظة:</strong> يتم تتبع الأجهزة تلقائياً عند فتح التطبيق. البيانات تشمل نوع الجهاز، الإصدار، وتاريخ التثبيت والتحديثات.
        </p>
      </div>
    </div>
  );
};

export default AppStatistics;
